import { Dialog } from "@headlessui/react";
import {
  MinusCircleIcon,
  PlusCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import FormInputComponent from "@via/components/inputs/form-input-component";
import { Spinner } from "@via/components/loading/loading";
import SelectComponent from "@via/components/select/select.tsx";
import UserGroupSelectComponent from "@via/components/select/user-group-select-component";
import { useAuth } from "@via/hooks/useAuth";
import {
  deduplicatePermissions,
  filterPermissions,
  transformEmptyStringsToNull,
  transformPermissions,
} from "@via/pages/data/premium-dataset/utils/helper";
import { useCreateVmetaField } from "@via/pages/projects/metadata/metadata.api.tsx";
import {
  PREMIUM_DATASET_PERMISSION_OPTIONS,
  USER_ROLES,
} from "@via/utils/constants";
import {
  addUserGroupMemberOptions,
  createMetadataFieldOptions,
  requiredValidation,
  objectValidation,
  arrayValidation,
  vmetaNameValidation,
} from "@via/utils/form-validators.tsx";
import { VmetaFieldType } from "common";
import { type FC, useEffect, useMemo, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";

interface CreateFieldFormProps {
  collectionId: string;
  setOpen: (open: boolean) => void;
}

const everyoneOption = { value: "everyone", label: "Everyone" };

const defaultPermission = {
  type: "user",
  email: "",
  read: true,
  write: false,
};

type PermissionMode = "inherit" | "custom";

const CreateFieldForm: FC<CreateFieldFormProps> = ({
  collectionId,
  setOpen,
}) => {
  const { user } = useAuth();
  const handleClose = () => setOpen(false);
  const [showAdvancedFields, setShowAdvancedFields] = useState(false);
  const [permissionMode, setPermissionMode] =
    useState<PermissionMode>("inherit");

  const {
    mutate: createField,
    isPending: isCreateFieldLoading,
    isSuccess: isCreateFieldSuccess,
  } = useCreateVmetaField();

  useEffect(() => {
    isCreateFieldSuccess && handleClose();
  }, [isCreateFieldSuccess]);

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<{
    name: string;
    label: string;
    type: VmetaFieldType;
    descriptive: boolean;
    unique: boolean;
    identifier: boolean;
    required: boolean;
    namingPattern: string | null;
    default: string | null;
    description: string | null;
    collectionID: string;
    unit: string | null;
    barcode: string | null;
    barcode_prefix: string | null;
    hidden: boolean;
    deprecated: boolean;
    checkvalid: any;
    ontology: any;
    order: number | null;
    ref: string | null;
    enum: any;
    min: any;
    max: any;
    lowercase: boolean;
    uppercase: boolean;
    trim: boolean | null;
    header: boolean | null;
    grouped: string | null;
    minlength: number | null;
    maxlength: number | null;
    perms: any[];
  }>({
    mode: "onSubmit",
    defaultValues: {
      name: "",
      label: "",
      type: VmetaFieldType.String,
      descriptive: false,
      unique: false,
      identifier: false,
      required: false,
      namingPattern: null,
      default: null,
      description: null,
      collectionID: collectionId,
      unit: null,
      barcode: null,
      barcode_prefix: null,
      hidden: false,
      deprecated: false,
      checkvalid: null,
      ontology: null,
      order: null,
      ref: null,
      enum: null,
      min: null,
      max: null,
      lowercase: false,
      uppercase: false,
      trim: null,
      header: null,
      grouped: null,
      minlength: null,
      maxlength: null,
      perms: [],
    },
  });

  const permissionOptions = useMemo(() => {
    if (user && user?.userRole === USER_ROLES.ADMIN) {
      return [everyoneOption, ...PREMIUM_DATASET_PERMISSION_OPTIONS];
    } else {
      return PREMIUM_DATASET_PERMISSION_OPTIONS;
    }
  }, [user]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: "perms" as never,
  });

  const handleCreateField = async payload => {
    const transformedPerms = transformPermissions(payload.perms);
    const filteredPerms = filterPermissions(transformedPerms);
    const uniquePerms = deduplicatePermissions(filteredPerms);

    const parsedEnum = payload.enum ? JSON.parse(payload.enum) : null;
    const payloadToRemoveEmptyStrings = transformEmptyStringsToNull(payload);
    // Always omit perms from the base payload; add it back only in "custom" mode.
    const { perms: _perms, ...payloadWithoutPerms } =
      payloadToRemoveEmptyStrings;

    const body: any = {
      ...payloadWithoutPerms,
      enum: parsedEnum,
    };

    // If "inherit" is selected, omit perms so backend can inherit from collection.
    if (permissionMode === "custom") {
      if (Array.isArray(uniquePerms) && uniquePerms.length > 0) {
        body.perms = uniquePerms;
      }
    }

    createField(body);
  };

  return (
    <form
      className="flex h-full flex-col divide-y divide-gray-200 bg-white shadow-xl"
      onSubmit={e => {
        e.preventDefault();
        void handleSubmit(handleCreateField)(e);
      }}>
      <div className="h-0 flex-1 overflow-y-auto">
        <div className="border-b bg-gray-50 px-4 py-6 sm:px-6">
          <div className="flex items-center justify-between">
            <Dialog.Title className="text-base font-semibold leading-6 text-gray-900">
              New Field
            </Dialog.Title>
            <div className="ml-3 flex h-7 items-center">
              <button
                type="button"
                className="relative rounded-md text-gray-900 duration-150 ease-in-out hover:text-gray-600 focus:outline-none"
                onClick={handleClose}>
                <span className="absolute -inset-2.5" />
                <span className="sr-only">Close panel</span>
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
          </div>
          <div className="mt-1">
            <p className="text-sm text-gray-500">
              Get started by filling in the information below to create your
              field.
            </p>
          </div>
        </div>
        <div className="flex flex-1 flex-col justify-between">
          <div className="divide-y divide-gray-200 px-2 sm:px-4">
            <div className="space-y-6 pb-5 pt-6">
              <FormInputComponent
                control={control}
                name="name"
                label="Name (used in API URLs)"
                placeholder="e.g. library_prep_kit"
                showRequiredField
                showTooltipIcon
                tooltipText={
                  'Used in API routes and URLs.<br/>Letters/numbers/spaces/-/_ only. No dots.<br/>Spaces become "_" and uppercase becomes lowercase.'
                }
                validations={vmetaNameValidation("Name (used in API URLs)")}
              />
              <FormInputComponent
                control={control}
                name="label"
                label="Label (Display name)"
                placeholder="e.g. Library prep kit (vendor + catalog #)"
                showRequiredField
                showTooltipIcon
                tooltipText={
                  "Shown in the UI (column header, filters). Any characters allowed."
                }
                validations={requiredValidation("Label")}
              />
              <div>
                <label className="mb-2 block text-sm font-medium leading-6 text-gray-900">
                  Type
                </label>
                <SelectComponent
                  control={control}
                  name="type"
                  rules={createMetadataFieldOptions.type}
                  enableClear={false}
                  options={
                    Object.values(VmetaFieldType)?.map(type => ({
                      label: type,
                      value: type,
                    })) ?? []
                  }
                  placeholder="Select Type"
                />
                {errors.type && (
                  <span className="text-xs text-rose-500">
                    {errors.type.message}
                  </span>
                )}
              </div>
              <div className="flex gap-4">
                <label className="flex items-center gap-1">
                  <FormInputComponent
                    control={control}
                    name="unique"
                    type="checkbox"
                    className="h-6 w-6 -mt-2"
                  />
                  Unique
                </label>
                <label className="flex items-center gap-1">
                  <FormInputComponent
                    control={control}
                    name="identifier"
                    type="checkbox"
                    className="h-6 w-6 -mt-2"
                  />
                  Identifier
                </label>
                <label className="flex items-center gap-1">
                  <FormInputComponent
                    control={control}
                    name="required"
                    type="checkbox"
                    className="h-6 w-6 -mt-2"
                  />
                  Required
                </label>
              </div>
              <FormInputComponent
                control={control}
                name="namingPattern"
                label="Naming Pattern"
                placeholder="Enter Naming Pattern"
              />
              <FormInputComponent
                control={control}
                name="default"
                label="Default"
                placeholder="Enter Default"
              />
              <FormInputComponent
                control={control}
                name="description"
                label="Description"
                placeholder="Enter Description"
              />
              <div className="flex flex-row items-center">
                <label className="mr-2 block text-sm font-medium leading-6 text-gray-900">
                  Show Advanced Fields
                </label>
                <div onClick={() => setShowAdvancedFields(!showAdvancedFields)}>
                  {showAdvancedFields ? (
                    <MinusCircleIcon className="h-5 w-5 cursor-pointer text-indigo-600 hover:text-indigo-500" />
                  ) : (
                    <PlusCircleIcon className="h-5 w-5 cursor-pointer text-indigo-600 hover:text-indigo-500" />
                  )}
                </div>
              </div>
              {showAdvancedFields && (
                <>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-1">
                      <FormInputComponent
                        control={control}
                        name="descriptive"
                        type="checkbox"
                        className="h-6 w-6 -mt-2"
                      />
                      Descriptive
                    </label>
                    <label className="flex items-center gap-1">
                      <FormInputComponent
                        control={control}
                        name="hidden"
                        type="checkbox"
                        className="h-6 w-6 -mt-2"
                      />
                      Hidden
                    </label>
                    <label className="flex items-center gap-1">
                      <FormInputComponent
                        control={control}
                        name="deprecated"
                        type="checkbox"
                        className="h-6 w-6 -mt-2"
                      />
                      Deprecated
                    </label>
                    <label className="flex items-center gap-1">
                      <FormInputComponent
                        control={control}
                        name="lowercase"
                        type="checkbox"
                        className="h-6 w-6 -mt-2"
                      />
                      Lowercase
                    </label>
                    <label className="flex items-center gap-1">
                      <FormInputComponent
                        control={control}
                        name="uppercase"
                        type="checkbox"
                        className="h-6 w-6 -mt-2"
                      />
                      Uppercase
                    </label>
                  </div>
                  <FormInputComponent
                    control={control}
                    name="unit"
                    label="Unit"
                    placeholder="Enter Unit"
                  />
                  <FormInputComponent
                    control={control}
                    name="grouped"
                    label="Grouped"
                    placeholder="Enter Grouped"
                  />
                  <FormInputComponent
                    control={control}
                    name="barcode"
                    label="Barcode Pattern"
                    placeholder="Enter Barcode Pattern"
                  />
                  <FormInputComponent
                    control={control}
                    name="barcode_prefix"
                    label="Barcode Prefix"
                    placeholder="Enter Barcode Prefix"
                  />
                  <FormInputComponent
                    control={control}
                    name="ontology"
                    label="Ontology"
                    placeholder="Enter Ontology"
                    validations={objectValidation(
                      "Ontology must be a valid object"
                    )}
                  />
                  <FormInputComponent
                    control={control}
                    name="checkvalid"
                    label="Checkvalid"
                    placeholder="Enter Checkvalid"
                  />
                  <FormInputComponent
                    control={control}
                    name="ref"
                    label="Ref"
                    placeholder="Enter Ref"
                  />
                  <FormInputComponent
                    control={control}
                    name="enum"
                    label="Enum"
                    placeholder="Enter Enum"
                    validations={arrayValidation("Enum must be a valid array")}
                  />
                  <FormInputComponent
                    control={control}
                    name="min"
                    label="Min"
                    type="number"
                    placeholder="Enter Min"
                  />
                  <FormInputComponent
                    control={control}
                    name="max"
                    label="Max"
                    type="number"
                    placeholder="Enter Max"
                  />
                  <FormInputComponent
                    control={control}
                    name="minlength"
                    label="Min Length"
                    type="number"
                    placeholder="Enter Min Length"
                  />
                  <FormInputComponent
                    control={control}
                    name="maxlength"
                    label="Max Length"
                    type="number"
                    placeholder="Enter Max Length"
                  />
                </>
              )}
              <div className="mt-2">
                <label className="flex items-center text-sm font-medium leading-6 text-gray-900">
                  Permissions
                </label>
                <div className="mt-2 space-y-2">
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="radio"
                      name="permissionMode"
                      value="inherit"
                      checked={permissionMode === "inherit"}
                      onChange={() => setPermissionMode("inherit")}
                    />
                    Inherit from collection (recommended)
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="radio"
                      name="permissionMode"
                      value="custom"
                      checked={permissionMode === "custom"}
                      onChange={() => setPermissionMode("custom")}
                    />
                    Custom
                  </label>
                </div>
                {permissionMode === "inherit" && (
                  <p className="mt-2 text-sm text-gray-500">
                    This field will inherit permissions from the collection.
                  </p>
                )}
              </div>
              {permissionMode === "custom" && (
                <>
                  <div className="flex justify-between text-center">
                    <label className="flex items-center text-sm font-medium leading-6 text-gray-900">
                      Fields Visibility & Configuration
                    </label>
                    <button
                      type="button"
                      onClick={() => append(defaultPermission)}
                      className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
                      + Add Permission
                    </button>
                  </div>
                  <div className="flex flex-col px-2 sm:px-4 max-h-[500px] overflow-auto">
                    {fields.map((field, idx) => {
                      const type = watch(`perms.${idx}.type`);
                      const isEveryone = type === "everyone";
                      return (
                        <div
                          key={field.id}
                          className="flex items-center gap-2 border-b py-2">
                          <div className="min-w-[150px]">
                            <SelectComponent
                              control={control}
                              name={`perms.${idx}.type`}
                              options={permissionOptions}
                              placeholder="Select type"
                              enableClear={false}
                            />
                          </div>
                          {type === "user" && (
                            <div className="-mt-2 w-full">
                              <FormInputComponent
                                control={control}
                                name={`perms.${idx}.email`}
                                placeholder="Enter email"
                                validations={addUserGroupMemberOptions.email}
                              />
                            </div>
                          )}
                          {type === "group" && (
                            <div className="w-full">
                              <UserGroupSelectComponent
                                control={control}
                                name={`perms.${idx}.groupId`}
                                rules={requiredValidation("User Group")}
                                menuPlacement="top"
                                errors={errors}
                                uniqueIdentifier="vmetaGroupId"
                              />
                            </div>
                          )}
                          <label className="flex items-center gap-1">
                            <FormInputComponent
                              control={control}
                              name={`perms.${idx}.read`}
                              type="checkbox"
                              className="h-6 w-6 -mt-2"
                            />
                            Read
                          </label>
                          {!isEveryone && (
                            <label className="flex items-center gap-1">
                              <FormInputComponent
                                control={control}
                                name={`perms.${idx}.write`}
                                type="checkbox"
                                className="h-6 w-6 mr-2 -mt-2"
                              />
                              Write
                            </label>
                          )}
                          <button
                            type="button"
                            onClick={() => remove(idx)}
                            className="px-2">
                            ×
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-shrink-0 justify-end px-4 py-4">
        <button
          id="create-field-cancel"
          type="button"
          className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          onClick={handleClose}>
          Cancel
        </button>
        <button
          id="create-field-save"
          type="submit"
          className="ml-4 inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
          <Spinner loading={isCreateFieldLoading}>Save</Spinner>
        </button>
      </div>
    </form>
  );
};

export default CreateFieldForm;
